import { IconButton, TextField } from "@mui/material";
import React, { Fragment, useState } from "react";
import { Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  fetchLogin,
  fetchLoginGoogle,
  resetPassword,
  resendVerification,
} from "../../actions/user";
import { UAParser } from "ua-parser-js";
import { toast } from "react-toastify";
import { CustomToast } from "./CustomToast";
import { useTranslation } from "react-i18next";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Checkbox, FormControlLabel } from "@mui/material";
import { CANONICAL_SALES_PATH } from "../../constants/salesRoutes";

const LoginForm = (props) => {
  const { hideModal, onSuccess } = props || {};
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const initialState = {
    email: "",
    password: "",
    deviceInfo: [],
  };

  const [data, setData] = useState(initialState);
  const [rememberMe, setRememberMe] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isResetVerification, setIsResetVerification] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;

    setData({
      ...data,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isForgotMode) {
      // 🔹 režim zaboravljene lozinke
      if (!data.email) {
        toast(
          <CustomToast
            type="error"
            title={t("error.error")}
            message={t("error.emailMsg")}
          />
        );
      }
      setIsForgotMode(false);
      setData(initialState);
      const dataVal = { email: data.email };
      dispatch(resetPassword(dataVal));
      return;
    }

    const emailFormat = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

    if (!emailFormat.test(data.email)) {
      setErrors({
        ...errors,
        email: t("error.emailInvalidError"),
      });
      return;
    }

    setErrors({ ...errors, email: null });

    const parser = new UAParser(navigator.userAgent);
    const deviceInfo = parser.getResult();
    const payload = {
      ...data,
      deviceInfo: deviceInfo,
    };

    dispatch(
      fetchLogin(
        payload,
        hideModal,
        location,
        navigate,
        rememberMe,
        onSuccess
      )
    );
  };

  const renderTooltip = (props) => (
    <Tooltip id="email-tooltip" {...props} className="custom-tooltip">
      {errors.email}
    </Tooltip>
  );

  const handleGoogleSuccessLogin = (credentialResponse) => {
    const parser = new UAParser(navigator.userAgent);
    const deviceInfo = parser.getResult();

    const payload = {
      credential: credentialResponse?.credential,
      deviceInfo: deviceInfo,
    };

    dispatch(
      fetchLoginGoogle(
        payload,
        hideModal,
        navigate,
        location,
        rememberMe,
        onSuccess
      )
    );
  };

  const handleVerificationModal = () => {
    setIsResetVerification(!isResetVerification);
    setData(initialState);
    setIsForgotMode(false);
  };

  const handleSubscribe = () => {
    hideModal?.();
  };

  const handleResetVerificationSubmit = (e) => {
    e.preventDefault();

    dispatch(resendVerification(data, handleVerificationModal, hideModal));
  };

  const isDefaultLogin = !isResetVerification && !isForgotMode;
  const modalTitle = isResetVerification
    ? t("login.resetVerificationTitle")
    : isForgotMode
    ? t("login.resetPasswordTitle")
    : t("global.loginTitle");

  return (
    <div className="login-modal-holder">
      <div className="login-modal-title">
        <h1>
          {modalTitle}
          {isDefaultLogin && (
            <>
              {" "}
              <span className="login-title-separator">ili</span>{" "}
              {t("global.loginTitleAlt")}
            </>
          )}
        </h1>
        <div className="login-modal-close-icon" onClick={hideModal}>
          <img src="/images/close.png" alt="" className="img-fluid" />
        </div>
      </div>
      <div className="login-modal-container">
        <div className="login-modal-form">
          {isResetVerification ? (
            <div className="login-modal-verification-holder">
              <Form onSubmit={handleResetVerificationSubmit} noValidate>
                <div className="login-modal-content">
                  <TextField
                    fullWidth
                    name="email"
                    type="text"
                    value={data.email}
                    onChange={handleInputChange}
                    className="input-text-type mb-3"
                    placeholder="E-mail"
                    error={!!errors.email}
                  />
                  <div
                    className="login-modal-forgot"
                    onClick={() => handleVerificationModal()}
                  >
                    {t("login.backToLogin")}
                  </div>

                  <div className="login-modal-submit">
                    <button type="submit">{t("login.sendRequest")}</button>
                  </div>
                </div>
              </Form>
            </div>
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <div className="login-modal-content">
                <OverlayTrigger
                  placement="bottom"
                  overlay={errors.email ? renderTooltip : <></>}
                  show={!!errors.email}
                >
                  <TextField
                    fullWidth
                    name="email"
                    type="text"
                    value={data.email}
                    onChange={handleInputChange}
                    className="input-text-type mb-3"
                    placeholder="E-mail"
                    error={!!errors.email}
                  />
                </OverlayTrigger>
                {!isForgotMode && (
                  <div className="position-relative login-password-holder-input">
                    <TextField
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={data.password}
                      onChange={handleInputChange}
                      className="input-text-type w-100"
                      placeholder={t("registration.password")}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        ),
                      }}
                    />
                  </div>
                )}
              </div>
              {/* link zaboravljena lozinka */}
              {!isForgotMode ? (
                <div className="login-holder-forgot-remember">
                  <div className="login-modal-remember">
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="rememberMe"
                          checked={rememberMe || false}
                          onChange={(e) =>
                            setRememberMe(e.target.checked || false)
                          }
                          size="small"
                          sx={{
                            padding: "4px",
                            color: "#fff",
                            "&.Mui-checked": {
                              color: "#fff", // zelena koju koristiš u adminu
                            },
                          }}
                        />
                      }
                      label={t("login.rememberMe")}
                    />
                  </div>
                  <div
                    className="login-modal-forgot"
                    onClick={() => setIsForgotMode(true)}
                  >
                    {t("login.forgotPassword")}
                  </div>
                </div>
              ) : (
                <div
                  className="login-modal-forgot"
                  onClick={() => setIsForgotMode(false)}
                >
                  {t("login.backToLogin")}
                </div>
              )}

              <div className="login-modal-submit">
                <button type="submit">
                  {isForgotMode ? t("login.sendRequest") : t("global.login")}
                </button>
              </div>
            </Form>
          )}
          <div className="login-modal-register-links-holder">
            <div className="login-modal-register-link-title">
              {t("login.loginWith")}
            </div>
            <div className="login-modal-register-links">
              {" "}
              <div className="login-modal-register-link">
                <GoogleLogin
                  locale="sr-Latn"
                  t
                  text="signin_with"
                  onSuccess={(credentialResponse) => {
                    handleGoogleSuccessLogin(credentialResponse);
                  }}
                  onError={() => {
                    toast(
                      <CustomToast
                        type="error"
                        title={t("error.error")}
                        message="Google login nije uspeo."
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
          <div className="login-modal-register">
            {t("login.subscribePrompt")}
            <div className="login-modal-submit login-modal-register-cta">
              <Link to={CANONICAL_SALES_PATH} onClick={handleSubscribe}>
                {t("subscription.subscribeCta")}
              </Link>
            </div>
            <div
              className="resend-verification-holder"
              onClick={() => handleVerificationModal()}
            >
              <span>Verifikacija naloga</span>
            </div>
          </div>
        </div>
        <div className="login-modal-banner">
          <img src="/images/logo-login-kat.png" alt="" className="img-fluid" />
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
